/**
 * Storage helper utilities for thread and resource IDs
 * Centralizes sessionStorage and localStorage logic
 */

// Storage keys
const STORAGE_KEYS = {
  THREAD_ID: "chatbot-thread-id",
  RESOURCE_ID: "chatbot-resource-id",
  CHATBOT_OPEN: "chatbot-is-open",
  LAST_ACTIVITY: "chatbot-last-activity"
};

// Thread expiry time in milliseconds (15 minutes)
const THREAD_EXPIRY_MS = 15 * 60 * 1000;

/**
 * Generate a new thread ID
 * @returns {string} New thread ID
 */
function generateThreadId() {
  return `thread-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Update last activity timestamp
 * Called when user sends a message
 */
export function updateLastActivity() {
  if (typeof window === "undefined") {
    return;
  }
  sessionStorage.setItem(STORAGE_KEYS.LAST_ACTIVITY, Date.now().toString());
}

/**
 * Check if thread has expired (15 minutes since last activity)
 * @returns {boolean} True if thread has expired
 */
export function isThreadExpired() {
  if (typeof window === "undefined") {
    return false;
  }
  const lastActivity = sessionStorage.getItem(STORAGE_KEYS.LAST_ACTIVITY);
  if (!lastActivity) {
    return false; // No activity recorded, thread is fresh
  }
  const elapsed = Date.now() - parseInt(lastActivity, 10);
  return elapsed >= THREAD_EXPIRY_MS;
}

/**
 * Create a new thread (for when thread expires)
 * Only creates new thread ID - messages are kept visible in UI
 * (Old messages remain but won't be sent to backend with new threadId)
 * @returns {string} New thread ID
 */
export function createNewThread() {
  if (typeof window === "undefined") {
    return generateThreadId();
  }
  // Clear old thread ID
  sessionStorage.removeItem(STORAGE_KEYS.THREAD_ID);
  // NOTE: Don't clear Zustand messages here - we want old messages to stay visible in UI
  // The newSessionStartIndexRef in useChatbotTransport handles which messages to send to backend
  // Generate and store new thread ID
  const newThreadId = generateThreadId();
  sessionStorage.setItem(STORAGE_KEYS.THREAD_ID, newThreadId);
  // Update last activity
  updateLastActivity();
  return newThreadId;
}

/**
 * Get or create a thread ID from sessionStorage
 * Thread IDs persist across the session to maintain conversation continuity
 * @returns {string} Thread ID
 */
export function getOrCreateThreadId() {
  if (typeof window === "undefined") {
    return generateThreadId();
  }

  const stored = sessionStorage.getItem(STORAGE_KEYS.THREAD_ID);
  if (stored) {
    return stored;
  }

  const newThreadId = generateThreadId();
  sessionStorage.setItem(STORAGE_KEYS.THREAD_ID, newThreadId);
  return newThreadId;
}

/**
 * Get resource ID from localStorage
 * Only returns recruiter_id if it exists, no temporary IDs
 * @returns {string|null} Resource ID or null if not available
 */
export function getOrCreateResourceId() {
  if (typeof window === "undefined") {
    return null;
  }
  // Only return stored resource ID if it exists (should be recruiter_id)
  const stored = localStorage.getItem(STORAGE_KEYS.RESOURCE_ID);
  return stored || null;
}

/**
 * Get chatbot open state from localStorage
 * Used to restore chatbot state after page navigation/redirect
 * @returns {boolean} Whether chatbot should be open
 */
export function getChatbotOpenState() {
  if (typeof window === "undefined") {
    return false;
  }
  const stored = localStorage.getItem(STORAGE_KEYS.CHATBOT_OPEN);
  return stored === "true";
}

/**
 * Set chatbot open state in localStorage
 * @param {boolean} isOpen - Whether chatbot is open
 */
export function setChatbotOpenState(isOpen) {
  if (typeof window === "undefined") {
    return;
  }
  localStorage.setItem(STORAGE_KEYS.CHATBOT_OPEN, isOpen ? "true" : "false");
}

/**
 * Clear chatbot open state from localStorage
 * Called when user explicitly closes the chatbot
 */
export function clearChatbotOpenState() {
  if (typeof window === "undefined") {
    return;
  }
  localStorage.removeItem(STORAGE_KEYS.CHATBOT_OPEN);
}

/**
 * Clear all chatbot storage (thread ID and resource ID)
 * Called during logout to ensure clean state for next user session
 */
export function clearChatbotStorage() {
  if (typeof window === "undefined") {
    return;
  }
  // Clear thread ID from sessionStorage
  sessionStorage.removeItem(STORAGE_KEYS.THREAD_ID);
  // Clear resource ID from localStorage
  localStorage.removeItem(STORAGE_KEYS.RESOURCE_ID);
  // Also clear chatbot open state
  localStorage.removeItem(STORAGE_KEYS.CHATBOT_OPEN);
  // Clear last activity timestamp
  sessionStorage.removeItem(STORAGE_KEYS.LAST_ACTIVITY);
  // Clear Zustand persisted messages (key used by useChatbotStore)
  sessionStorage.removeItem("chatbot-messages-storage");
}
